import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Fade,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Paper,
  styled,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  MenuItem,
  Select,
  FormControl,
  IconButton
} from '@mui/material';
import {
  Group as TeamIcon,
  Person as SelfIcon,
  MonetizationOn as CommissionIcon,
  Percent as PercentIcon,
  BusinessCenter as BasisIcon,
  Edit as EditIcon,
  Check as CheckIcon
} from '@mui/icons-material';

const SettingsSwitch = styled(Switch)(({ theme }) => ({
  width: 42,
  height: 24,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(18px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#6366f1',
        opacity: 1,
        border: 0,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 20,
    height: 20,
  },
  '& .MuiSwitch-track': {
    borderRadius: 24 / 2,
    backgroundColor: '#E9E9EA',
    opacity: 1,
  },
}));

const CommissionSetting = () => {
  const [settings, setSettings] = useState({ 
    team: true, 
    self: true,
    minEligiblePercentage: 5,
    businessBasis: 'both'
  });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [isEditingExtra, setIsEditingExtra] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/levels/settings');
      if (response.data.status) {
        const commission = response.data.data.commissionSettings;
        setSettings({
          team: commission.enableTeamBusiness,
          self: commission.enableSelfBusiness,
          minEligiblePercentage: response.data.data.minEligiblePercentage || 0,
          businessBasis: commission.businessBasis || 'both'
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setNotification({ open: true, message: 'Failed to load settings', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (updates) => {
    const newSettings = { ...settings, ...updates };
    setUpdating(true);
    try {
      const current = await axios.get('/api/levels/settings');
      const payload = {
        ...current.data.data,
        minEligiblePercentage: Number(newSettings.minEligiblePercentage)
      };

      const response = await axios.put('/api/levels/settings', payload);
      if (response.data.status) {
        setSettings(newSettings);
        setIsEditingExtra(false);
        setNotification({ open: true, message: 'Settings updated successfully', severity: 'success' });
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      setNotification({ open: true, message: 'Failed to update settings', severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 3, md: 6 }, bgcolor: '#fff', minHeight: '100vh' }}>
      <Fade in timeout={500}>
        <Box sx={{ maxWidth: 800 }}>
          
          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
            <CommissionIcon sx={{ fontSize: 32, color: '#6366f1' }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a1f2c' }}>
                Commission Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage global distribution rules for percentage-based sales commissions.
              </Typography>
            </Box>
          </Box>

          <Paper elevation={0} sx={{ border: '1px solid #f1f5f9', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
            {updating && (
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(255,255,255,0.5)', zIndex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            )}
            <List sx={{ p: 0 }}>
              <ListItem sx={{ py: 3, px: 3 }}>
                <PercentIcon sx={{ mr: 2, color: '#f59e0b', fontSize: 24 }} />
                <ListItemText 
                   primary={<Typography variant="h6" sx={{ fontWeight: 700 }}>Minimum Eligibility Percentage</Typography>}
                   secondary="Global commission threshold required to qualify for all reward and incentive programs."
                />
                <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    type="number"
                    size="small"
                    value={settings.minEligiblePercentage}
                    onChange={(e) => setSettings({ ...settings, minEligiblePercentage: e.target.value })}
                    sx={{ width: 120, '& input': { textAlign: 'center', fontWeight: 800, fontSize: '1.1rem' } }}
                    disabled={!isEditingExtra || updating}
                  />
                  {isEditingExtra ? (
                    <IconButton 
                      size="medium" 
                      onClick={() => updateSettings({})} 
                      sx={{ bgcolor: '#6366f1', color: '#fff', '&:hover': { bgcolor: '#4f46e5' }, boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)' }}
                    >
                      <CheckIcon />
                    </IconButton>
                  ) : (
                    <IconButton 
                      size="medium" 
                      onClick={() => setIsEditingExtra(true)} 
                      sx={{ bgcolor: '#f1f5f9', color: '#64748b' }}
                    >
                      <EditIcon />
                    </IconButton>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </Paper>

          <Box sx={{ mt: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px dashed #e2e8f0' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              Operational Note: Sales commissions are automatically adjusted based on these distribution rules.
            </Typography>
          </Box>

        </Box>
      </Fade>

      <Snackbar 
        open={notification.open} 
        autoHideDuration={4000} 
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={notification.severity} variant="filled" sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommissionSetting;
